import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EventNote as EventNoteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { useLessonPlanListHook } from "../../../hooks/useLessonPlanHook";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { TLessonPlanStatus } from "../../../apis/lessonPlan/lessonPlan.interfaces";
import {
  formatStatusLabel,
  formatTerm,
  lessonPlanStatusOptions,
  lessonPlanTermOptions,
  renderLessonPlanStatusChip,
} from "./lessonPlan.helpers";
import { LessonPlanViewProps } from "./lessonPlan.interfaces";
import ManageLessonPlanDialog from "./manageLessonPlanDialog";

const PAGE_SIZE = 20;

const LessonPlanView: FC<LessonPlanViewProps> = () => {
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();
  const { data: classes } = useClassHook();
  const { data: subjects } = useSubjectsHook();

  const [page, setPage] = useState(1);
  const [classFilter, setClassFilter] = useState<string>("");
  const [subjectFilter, setSubjectFilter] = useState<string>("");
  const [termFilter, setTermFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      classId: classFilter || undefined,
      courseId: subjectFilter || undefined,
      term: termFilter || undefined,
      status: statusFilter || undefined,
      search: searchQuery || undefined,
    }),
    [page, classFilter, subjectFilter, termFilter, statusFilter, searchQuery],
  );

  const { data, isLoading, error } = useLessonPlanListHook(filters);
  const plans = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const classNameById = useMemo(() => {
    const map = new Map<string, string>();
    classes?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [classes]);

  const subjectLabelById = useMemo(() => {
    const map = new Map<string, string>();
    subjects?.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [subjects]);

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.lessonPlan} />
        <Box p={3} data-testid="lesson-plan-view">
          <MainTitle title="Lesson Plans" />
          <Alert severity="info" data-testid="lesson-plan-no-institute">
            Please select an institute from the dashboard to view lesson plans.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.lessonPlan} />
      <Grid container spacing={3} data-testid="lesson-plan-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <MainTitle title="Lesson Plans" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={() => setCreateDialogOpen(true)}
              data-testid="add-lesson-plan-button"
            >
              Add Lesson Plan
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="lesson-plan-filters-card">
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="lesson-plan-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by title, subject, grade..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "lesson-plan-search-input" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={classFilter}
                    onChange={(e) => {
                      setClassFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="lesson-plan-class-filter"
                  >
                    <MenuItem value="">All Classes</MenuItem>
                    {classes?.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={subjectFilter}
                    onChange={(e) => {
                      setSubjectFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="lesson-plan-subject-filter"
                  >
                    <MenuItem value="">All subjects</MenuItem>
                    {subjects?.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={termFilter}
                    onChange={(e) => {
                      setTermFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="lesson-plan-term-filter"
                  >
                    <MenuItem value="">All Terms</MenuItem>
                    {lessonPlanTermOptions.map((t) => (
                      <MenuItem key={t} value={t}>
                        {formatTerm(t)}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="lesson-plan-status-filter"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {lessonPlanStatusOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {formatStatusLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {isLoading ? (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              data-testid="lesson-plan-loading"
            >
              <CircularProgress />
            </Box>
          </Grid>
        ) : error ? (
          <Grid item xs={12}>
            <Alert severity="error" data-testid="lesson-plan-error">
              Failed to load lesson plans. Please try again later.
            </Alert>
          </Grid>
        ) : plans.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="lesson-plan-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                >
                  <EventNoteIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {totalCount === 0
                      ? "No lesson plans configured"
                      : "No lesson plans match your filters"}
                  </Typography>
                  {totalCount === 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateDialogOpen(true)}
                      data-testid="add-lesson-plan-empty-button"
                    >
                      Add Lesson Plan
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="lesson-plan-count"
              >
                Lesson Plans ({totalCount})
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                data-testid="lesson-plan-table-container"
              >
                <Table data-testid="lesson-plan-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Academic Year</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow
                        key={plan.id}
                        hover
                        data-testid={`lesson-plan-row-${plan.id}`}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {plan.title}
                            </Typography>
                            {plan.subject && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {plan.subject}
                                {plan.gradeLevel
                                  ? ` · ${plan.gradeLevel}`
                                  : ""}
                              </Typography>
                            )}
                            {plan.tags && plan.tags.length > 0 && (
                              <Box
                                display="flex"
                                gap={0.5}
                                flexWrap="wrap"
                                sx={{ mt: 0.5 }}
                              >
                                {plan.tags.map((tag) => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {plan.class?.name ??
                            (plan.class?.id
                              ? classNameById.get(plan.class.id) ?? "—"
                              : "—")}
                        </TableCell>
                        <TableCell>
                          {plan.course?.name
                            ? `${plan.course.name}${plan.course.code ? ` (${plan.course.code})` : ""}`
                            : plan.course?.id
                              ? subjectLabelById.get(plan.course.id) ?? "—"
                              : "—"}
                        </TableCell>
                        <TableCell>{plan.academicYear}</TableCell>
                        <TableCell>{formatTerm(plan.term)}</TableCell>
                        <TableCell>
                          {renderLessonPlanStatusChip(
                            plan.status as TLessonPlanStatus,
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(
                                `${pages.lessonPlan.path}/${plan.id}`,
                              )
                            }
                            data-testid={`view-lesson-plan-${plan.id}`}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    data-testid="lesson-plan-pagination"
                  />
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <ManageLessonPlanDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </>
  );
};

export default LessonPlanView;
